import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchMyTasksScope,
  searchMyTasksUsers,
} from '../../services/myTasksApi';

export type CollaboratorOption = {
  userId: string;
  name: string;
  email?: string | null;
  isSelf?: boolean;
  isSubordinate?: boolean;
};

type CollaboratorAutocompleteProps = {
  valueUserId?: string | null;
  valueUserName?: string | null;
  disabled?: boolean;
  saving?: boolean;
  emptyLabel?: string;
  /** Quando informado, usa esta lista em vez do escopo de tarefas. */
  optionsSource?: CollaboratorOption[];
  allowClear?: boolean;
  clearLabel?: string;
  onCommit: (user: CollaboratorOption | null) => void | Promise<void>;
};

type MenuPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

const AVATAR_TONES = [
  'bg-emerald-600',
  'bg-violet-600',
  'bg-sky-600',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-amber-600',
];

function collaboratorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function avatarTone(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index) * (index + 1)) % AVATAR_TONES.length;
  }
  return AVATAR_TONES[hash] ?? AVATAR_TONES[0];
}

function matchesQuery(user: CollaboratorOption, query: string): boolean {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return (
    user.name.toLowerCase().includes(term)
    || Boolean(user.email?.toLowerCase().includes(term))
  );
}

function mergeUsers(...groups: CollaboratorOption[][]): CollaboratorOption[] {
  const map = new Map<string, CollaboratorOption>();
  for (const group of groups) {
    for (const user of group) {
      map.set(user.userId, user);
    }
  }
  return Array.from(map.values());
}

function uniqueMatch(users: CollaboratorOption[], query: string): CollaboratorOption | null {
  const term = query.trim().toLowerCase();
  if (!term) return null;

  const exact = users.filter(
    (user) =>
      user.name.toLowerCase() === term
      || user.email?.toLowerCase() === term,
  );
  if (exact.length === 1) return exact[0];

  const partial = users.filter((user) => matchesQuery(user, term));
  if (partial.length === 1) return partial[0];

  return null;
}

export function CollaboratorAutocomplete({
  valueUserId,
  valueUserName,
  disabled = false,
  saving = false,
  emptyLabel = 'Não atribuído',
  optionsSource,
  allowClear = false,
  clearLabel = 'Remover responsável',
  onCommit,
}: CollaboratorAutocompleteProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggested, setSuggested] = useState<CollaboratorOption[]>([]);
  const [searchResults, setSearchResults] = useState<CollaboratorOption[]>([]);
  const [canSearchCompanyUsers, setCanSearchCompanyUsers] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    top: 0,
    left: 0,
    width: 320,
    maxHeight: 288,
  });
  const [loading, setLoading] = useState(false);
  const usesExternalSource = optionsSource != null;

  const selected = useMemo<CollaboratorOption | null>(
    () =>
      valueUserId
        ? {
            userId: valueUserId,
            name: valueUserName?.trim() || 'Colaborador',
          }
        : null,
    [valueUserId, valueUserName],
  );

  const options = useMemo(() => {
    const merged = mergeUsers(
      usesExternalSource ? optionsSource ?? [] : suggested,
      usesExternalSource ? [] : searchResults,
      selected ? [selected] : [],
    );
    return merged
      .filter((user) => matchesQuery(user, query))
      .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));
  }, [suggested, searchResults, selected, query, usesExternalSource, optionsSource]);

  function updateMenuPosition(): void {
    const rect = inputRef.current?.getBoundingClientRect() ?? rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 280);
    const maxLeft = window.innerWidth - width - 8;
    const gap = 4;
    const padding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const preferredMax = 288;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const available = Math.max(120, (openUpward ? spaceAbove : spaceBelow) - gap);
    const maxHeight = Math.min(preferredMax, available);
    setMenuPosition({
      top: openUpward ? rect.top - gap - maxHeight : rect.bottom + gap,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
      width,
      maxHeight,
    });
  }

  useEffect(() => {
    if (usesExternalSource) {
      setSuggested(optionsSource ?? []);
      setCanSearchCompanyUsers(false);
      return;
    }

    let cancelled = false;

    void fetchMyTasksScope()
      .then((scope) => {
        if (cancelled) return;
        setCanSearchCompanyUsers(scope.canSearchCompanyUsers);
        setSuggested(scope.defaultUsers);
      })
      .catch(() => {
        if (!cancelled) setSuggested([]);
      });

    return () => {
      cancelled = true;
    };
  }, [usesExternalSource, optionsSource]);

  useEffect(() => {
    if (usesExternalSource || !open || !canSearchCompanyUsers || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoading(true);
      void searchMyTasksUsers(query.trim())
        .then((users) => {
          if (!cancelled) setSearchResults(users);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, canSearchCompanyUsers, query, usesExternalSource]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, options.length]);

  async function commit(user: CollaboratorOption): Promise<void> {
    setOpen(false);
    setQuery('');
    setSearchResults([]);
    if (user.userId === valueUserId) return;
    await onCommit(user);
  }

  async function clearAssignee(): Promise<void> {
    setOpen(false);
    setQuery('');
    setSearchResults([]);
    if (!valueUserId) return;
    await onCommit(null);
  }

  const dismissAndCommit = useCallback(async (): Promise<void> => {
    const match = uniqueMatch(options, query);
    setOpen(false);
    setQuery('');
    setSearchResults([]);
    if (!match || match.userId === valueUserId) return;
    await onCommit(match);
  }, [onCommit, options, query, valueUserId]);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node | null;
      if (rootRef.current?.contains(target)) return;
      const menu = document.getElementById(listId);
      if (menu?.contains(target)) return;
      void dismissAndCommit();
    }

    function handleReposition(): void {
      updateMenuPosition();
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, listId, query, options, valueUserId, dismissAndCommit]);

  function openEditor(): void {
    if (disabled || saving) return;
    setQuery(selected?.name ?? '');
    setOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      updateMenuPosition();
    }, 0);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((current) => Math.min(current + 1, Math.max(options.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const user = options[highlightedIndex];
      if (user) void commit(user);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
      setSearchResults([]);
    }
  }

  const displayName = selected?.name ?? emptyLabel;

  return (
    <div ref={rootRef} className="relative">
      {open ? (
        <input
          ref={inputRef}
          className="erp-input"
          value={query}
          disabled={disabled || saving}
          placeholder="Buscar colaborador por nome ou e-mail"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-left hover:border-primary-300 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled || saving}
          onClick={openEditor}
        >
          {selected ? (
            <span
              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarTone(selected.userId)}`}
            >
              {collaboratorInitials(selected.name)}
            </span>
          ) : (
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-xs font-semibold text-secondary-600">
              ?
            </span>
          )}
          <span className={`min-w-0 truncate text-sm ${selected ? 'font-medium text-secondary-900' : 'text-secondary-500'}`}>
            {saving ? 'Salvando…' : displayName}
          </span>
        </button>
      )}

      {open
        ? createPortal(
            <ul
              id={listId}
              role="listbox"
              className="fixed z-[120] overflow-y-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-xl"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }}
            >
              {loading ? (
                <li className="px-3 py-2 text-sm text-secondary-500">Buscando…</li>
              ) : null}
              {allowClear && valueUserId ? (
                <li>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-secondary-600 hover:bg-secondary-50"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => void clearAssignee()}
                  >
                    {clearLabel}
                  </button>
                </li>
              ) : null}
              {options.length === 0 && !loading ? (
                <li className="px-3 py-2 text-sm text-secondary-500">
                  {query.trim().length < 2 && canSearchCompanyUsers
                    ? 'Digite pelo menos 2 letras para buscar na empresa.'
                    : 'Nenhum colaborador encontrado.'}
                </li>
              ) : (
                options.map((user, index) => (
                  <li key={user.userId} role="option" aria-selected={index === highlightedIndex}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left ${
                        index === highlightedIndex ? 'bg-secondary-100' : 'hover:bg-secondary-50'
                      }`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onPointerDown={(event) => event.preventDefault()}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => void commit(user)}
                    >
                      <span
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarTone(user.userId)}`}
                      >
                        {collaboratorInitials(user.name)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-secondary-900">
                          {user.name}
                          {user.isSelf ? ' (eu)' : ''}
                        </span>
                        {user.email ? (
                          <span className="block truncate text-xs text-secondary-500">{user.email}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
